import React from "react";
import { CreditCard, Clock4, ShieldCheck, Sparkles } from "lucide-react";

const STEPS = [
  {
    icon: Sparkles,
    title: "Choisissez",
    text: "Sélectionnez votre garde-temps parmi nos pièces neuves ou d'occasion certifiées.",
  },
  {
    icon: CreditCard,
    title: "Simulez",
    text: "Recevez en 24h une simulation personnalisée — durée, mensualité, sans engagement.",
  },
  {
    icon: Clock4,
    title: "Réglez à votre rythme",
    text: "Paiement échelonné jusqu'à 36 mois, sans frais cachés et sans dossier complexe.",
  },
  {
    icon: ShieldCheck,
    title: "Profitez",
    text: "Votre montre vous est remise après acceptation, garantie maison de 24 mois incluse.",
  },
];

const EXAMPLES = [
  {
    brand: "Rolex Submariner",
    monthly: "150.-",
    total: "CHF 14'900",
    months: 36,
    image:
      "https://images.unsplash.com/photo-1662384197911-e82189f4dc60?fm=jpg&q=85&w=1000&auto=format&fit=crop",
  },
  {
    brand: "Omega Speedmaster",
    monthly: "85.-",
    total: "CHF 7'200",
    months: 36,
    image:
      "https://images.unsplash.com/photo-1556453007-ee036169934b?fm=jpg&q=85&w=1000&auto=format&fit=crop",
  },
  {
    brand: "Cartier Tank",
    monthly: "120.-",
    total: "CHF 9'800",
    months: 36,
    image:
      "https://images.unsplash.com/photo-1622434641406-a158123450f9?fm=jpg&q=85&w=1000&auto=format&fit=crop",
  },
];

export default function Paiement() {
  return (
    <section
      id="paiement"
      data-testid="paiement-section"
      className="bg-[#001226] text-white py-16 md:py-32 relative overflow-hidden"
    >
      {/* Subtle orange glow */}
      <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full bg-accent/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full bg-accent/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 mb-20">
          <div className="lg:col-span-7">
            <p className="text-[10px] uppercase tracking-[0.4em] text-accent mb-6">
              Paiement Échelonné
            </p>
            <h2 className="font-serif font-light text-white text-4xl sm:text-5xl lg:text-6xl leading-[1] tracking-tight">
              Une Rolex,
              <br />
              <span className="italic">une mensualité.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-zinc-200 font-light leading-relaxed text-base md:text-lg self-end">
            Acquérez votre montre d'exception sans déposer 15'000 francs sur la
            table. Notre programme de paiement échelonné rend la haute
            horlogerie accessible — en toute transparence.
          </p>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10 mb-20">
          {STEPS.map((s, i) => {
            const Icon = s.icon;
            return (
              <div
                key={s.title}
                data-testid={`paiement-step-${i}`}
                className="bg-[#001226] p-8 md:p-10 flex flex-col gap-4"
              >
                <div className="flex items-baseline justify-between">
                  <Icon size={24} className="text-accent" />
                  <span className="font-serif text-3xl text-white/20">
                    0{i + 1}
                  </span>
                </div>
                <h3 className="font-serif text-2xl text-white font-light mt-2">
                  {s.title}
                </h3>
                <p className="text-zinc-300 text-sm font-light leading-relaxed">
                  {s.text}
                </p>
              </div>
            );
          })}
        </div>

        {/* Examples */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {EXAMPLES.map((e) => (
            <div
              key={e.brand}
              data-testid={`paiement-example-${e.brand}`}
              className="border border-white/15 hover:border-accent transition-colors group overflow-hidden flex flex-col"
            >
              <div className="aspect-[4/3] overflow-hidden bg-white/5">
                <img
                  src={e.image}
                  alt={e.brand}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  loading="lazy"
                />
              </div>
              <div className="p-8 flex flex-col flex-1">
                <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-400 mb-3">
                  Exemple
                </p>
                <p className="font-serif text-2xl text-white font-light leading-tight">
                  {e.brand}
                </p>
                <div className="mt-auto pt-6 border-t border-white/10 flex items-baseline gap-2">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-accent">
                    Dès
                  </span>
                  <span className="font-serif text-5xl text-white font-light">
                    {e.monthly}
                  </span>
                  <span className="text-zinc-400 text-sm">/mois</span>
                </div>
                <p className="mt-4 text-xs text-zinc-400">
                  {e.total} sur {e.months} mois · Sans frais cachés
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-between gap-6">
          <p className="text-xs text-zinc-400 max-w-xl leading-relaxed">
            * Sous réserve d'acceptation de votre dossier. Simulation
            personnalisée et sans engagement. Conditions détaillées
            communiquées par notre conseiller.
          </p>
          <a
            href="#simulateur"
            data-testid="paiement-cta"
            className="inline-flex items-center gap-3 px-10 py-4 bg-accent text-white text-[11px] uppercase tracking-[0.25em] hover:bg-accent-dark transition-colors"
          >
            Simuler ma mensualité
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
